export const en = {
    translation: {
        errorCodes: {
            noTenantAccess: 'No access to tenant',
        },
        navigation: {
            start: 'My tenants',
            myProfile: 'My account',
            tenant: {
                home: 'Home',
                users: 'Administrators',
                authenticationHeader: 'Authentication',
                settings: 'Tenant',
                authProviders: 'Sign-in methods',
                applications: 'Applications',
                identities: 'Identities',
                apis: 'APIs',
                otherHeader: 'Other',
                developer: 'API & Developer',
            },
        },
        home: {
            welcome: 'Welcome',
            intro: 'We are happy to have you here!',
            newTenantDialog: {
                title: 'New tenant',
                name: 'Tenant name',
                language: 'Default language',
                country: 'Default country',
                createTenant: 'Create tenant',
            },
            tenants: 'Your tenants',
            newTenant: 'Create a new tenant',
        },
        tenant: {
            home: {
                title: 'Overview',
                statistics: {
                    identities: 'Identities',
                    applications: 'Applications',
                    recentRegistrations: 'Recent registrations',
                    recentlyActive: 'Recently active',
                    lastSevenDays: 'Last 7 days',
                },
                info: { title: 'Connection details', tenantId: 'Tenant ID', issuerUrl: 'Issuer URL' },
            },
            error: {
                unknown: 'Unknown error',
                'missing.tenant': 'Unable to query the tenant, you might not have access',
            },
            applications: {
                title: 'Applications',
                list: {
                    title: 'Applications',
                    sidebar: {
                        title: 'Applications',
                    },
                    toolbar: {
                        title: 'Applications',
                        counter_zero: 'No applications',
                        counter_one: '{{count, number}} application',
                        counter_other: '{{count, number}} applications',
                        search: 'Name, Description',
                        newApplication: 'Create application',
                    },
                    empty: {
                        title: 'No applications',
                        subtitle: 'Create a new application to get started',
                        actionCreate: 'Create an application',
                    },
                    table: {
                        clientId: 'Client ID',
                        systemBadge: 'System',
                        typeValues: {
                            native: 'Native',
                            machine: 'Machine-to-Machine',
                            web: 'Regular Web Application',
                            spa: 'Single Page Application',
                        },
                    },
                    newApplication: {
                        title: 'Create application',
                        name: 'Name',
                        description: 'Description',
                        thirdParty: 'Third party',
                        type: 'Type',
                        typeValues: {
                            native: {
                                label: 'Native',
                                info: 'Mobile, desktop, CLI and smart device apps running natively.',
                            },
                            machine: {
                                label: 'Machine-to-Machine',
                                info: 'CLIs, daemons or services running on your backend.',
                            },
                            web: {
                                label: 'Regular Web Application',
                                info: 'Traditional web app using redirects.',
                            },
                            spa: {
                                label: 'Single Page Application',
                                info: 'Modern web app using browser-based routing and APIs.',
                            },
                        },
                    },
                },
                application: {
                    error: {
                        invalidUrl: 'Invalid URL',
                    },
                    header: {
                        general: 'General',
                        authentication: 'Authentication',
                        apis: 'APIs',
                        experience: 'Experience',
                        appearance: 'Appearance',
                        security: 'Security',
                        clientId: 'Client ID',
                        systemBadge: 'System',
                        typeValues: {
                            native: 'Native',
                            machine: 'Machine-to-Machine',
                            web: 'Regular Web Application',
                            spa: 'Single Page Application',
                        },
                    },
                    general: {
                        basicInfo: {
                            title: 'Basic information',
                            subtitle:
                                'Define how your application appears to users, including its name and description',
                            name: 'Name',
                            description: 'Description',
                        },
                        identity: {
                            title: 'Identity',
                            subtitle: 'Review the application type and client identifier used by this integration',
                            type: 'Application type',
                            clientId: 'Client ID',
                        },
                        trust: {
                            title: 'Trust',
                            subtitle:
                                'Choose whether this application is managed by your organization or by an external partner',
                            trusted: {
                                title: 'Trusted application',
                                description: 'Use this for applications your organization owns and operates.',
                            },
                            thirdParty: {
                                title: 'Third-party application',
                                description: 'Use this for external applications connecting to your tenant.',
                            },
                        },
                    },
                    authentication: {
                        authProviders: {
                            title: 'Sign-in methods',
                            subtitle: 'Choose which tenant sign-in methods are available in this application.',
                            inherit: 'Use tenant sign-in methods',
                            providers: {
                                local: 'Email & password',
                                google: 'Google',
                                microsoft: 'Microsoft',
                            },
                            unavailable: 'Not configured for this tenant.',
                            empty: 'No sign-in methods are configured in the tenant settings.',
                            noMethods:
                                'No sign-in method is available with this selection. Existing sessions and handoff tickets still work.',
                        },
                        urls: {
                            title: 'Authentication URLs',
                            subtitle: 'Define endpoints for authentication, identity syncronization, and CORS policies',
                            callbackUrls: 'Callback URLs',
                            allowedCorsOrigins: 'Allowed CORS origins',
                            identitySyncUrl: 'Identity sync URL',
                            appUrl: 'Application login URL',
                        },
                        authMethods: {
                            title: 'Authentication flows',
                            subtitle: 'Manage authentication flows, token lifetimes, and security requirements',
                            grantTypes: 'Grant types',
                            accessTokenTimeout: 'Access token timeout',
                            refreshTokenTimeout: 'Refresh token timeout',
                            clientCredentialsTimeout: 'Client credentials timeout',
                            idTokenTimeout: 'ID token timeout',
                            authCodeTimeout: 'Authentication code timeout',
                            pkceRequired: 'PKCE required',
                            pkceRequiredInfo:
                                'Require PKCE when exchanging authorization codes for tokens. This helps prevent intercepted codes from being used by another application.',
                            dpopRequired: 'Require DPoP',
                            dpopImplicitInfo: 'DPoP cannot be required while the implicit grant is enabled.',
                            dpopRequiredInfo:
                                'Require proof of possession when requesting access tokens. The application must support DPoP.',
                            grantTypeValues: {
                                implicit: 'Implicit',
                                authorization_code: 'Authorization code',
                                refresh_token: 'Refresh token',
                                client_credentials: 'Client credentials',
                            },
                            grantTypeInfos: {
                                implicit:
                                    'A legacy flow where the access token is issued directly in the authorization response, typically used in browser-based apps but now discouraged due to security concerns.',
                                authorization_code:
                                    'A secure, two-step flow where the client first receives an authorization code and then exchanges it for an access token, commonly used by web and mobile apps.',
                                refresh_token:
                                    'A mechanism that allows clients to obtain a new access token without requiring user interaction, improving security and user experience.',
                                client_credentials:
                                    'A flow where the client authenticates itself (without user involvement) to obtain an access token, often used for machine-to-machine communication.',
                            },
                            clientCredentialsNotAllowed:
                                'To enable the client credentials flow, configure an authentication method in the "Security" tab.',
                            grantTypeTypeRestriction: {
                                spa: 'Single Page Applications cannot use the "Client Credentials" flow because they are public clients.',
                                native: 'Native Applications cannot use the "Client Credentials" flow because they are public clients.',
                            },
                        },
                        handoff: {
                            title: 'Application handoff',
                            subtitle:
                                'Control whether this application can create or accept tickets for signing in across applications.',
                            issueTickets: 'Allow this application to create handoff tickets',
                            acceptTickets: 'Allow users to sign in to this application with a handoff ticket',
                        },
                        logout: {
                            title: 'Logout',
                            subtitle: 'Set up logout behavior, including redirects and confirmation prompts',
                            postLogoutUrls: 'Post logout redirect URLs',
                            backchannelLogoutUrl: 'Backchannel logout URL',
                            alwaysShowLogoutPrompt: 'Always show logout confirmation prompt',
                        },
                        timing: {
                            title: 'Sign-in timing',
                            subtitle:
                                'Control how long sign-in sessions stay active and how long a consent grant can be reused',
                            interactionTimeout: 'Sign-in session timeout',
                            grantTimeout: 'Consent lifetime',
                        },
                    },
                    apis: {
                        title: 'API access',
                        subtitle:
                            'Choose which APIs this application can request and which one should be used by default',
                        allowedResourceIds: 'Allowed APIs',
                        defaultResourceId: 'Default API',
                        noDefault: 'No default API',
                        disabled: 'Disabled',
                        empty: {
                            title: 'No APIs are available yet',
                            description:
                                'Add an API in tenant settings before assigning API access to this application.',
                        },
                    },
                    experience: {
                        policies: {
                            title: 'Policies',
                            subtitle: 'Add links shown to users during authentication and consent flows',
                            termsOfServiceUrl: 'Terms of service URL',
                            privacyPolicyUrl: 'Privacy policy URL',
                        },
                        footerLinks: {
                            title: 'Footer links',
                            subtitle: 'Manage the links shown in the footer of the authentication screens',
                            empty: 'No footer links have been added yet.',
                            add: 'Add link',
                            itemFallback: 'Link {{index}}',
                            label: 'Label',
                            url: 'URL',
                        },
                        signupAgreements: {
                            title: 'Sign-up agreements',
                            subtitle: 'Add agreements users must accept during sign-up',
                            empty: 'No sign-up agreements have been added yet.',
                            add: 'Add agreement',
                            itemFallback: 'Agreement {{index}}',
                            text: 'Agreement text',
                        },
                    },
                    security: {
                        clientAuthentication: {
                            title: 'Client authentication',
                            subtitle: 'Configure how your application authenticates with the authorization server',
                            endpointsAuthMethod: 'Authentication method',
                            clientSecret: 'Client secret',
                            authMethodValues: {
                                client_secret_basic: 'Client Secret (Basic Authentication)',
                                client_secret_jwt: 'Client Secret (JWT Assertion)',
                                client_secret_post: 'Client Secret (POST Parameter)',
                                private_key_jwt: 'Private Key (JWT Assertion)',
                                none: 'No Authentication',
                            },
                            authMethodInfos: {
                                client_secret_basic:
                                    'The application authenticates using a shared secret, sent as a Basic Auth header in requests.',
                                client_secret_jwt:
                                    'The application generates a signed JWT using its shared secret for authentication.',
                                client_secret_post:
                                    'The application sends its shared secret in the body of the request instead of the header.',
                                private_key_jwt: 'The application signs a JWT using a private key for authentication.',
                                none: 'The application does not use any authentication mechanism.',
                            },
                            authMethodTypeRestriction: {
                                spa: 'Single Page Applications cannot use authentication methods because they are public clients.',
                                native: 'Native Applications cannot use authentication methods because they are public clients.',
                            },
                        },
                        publicKeys: {
                            title: 'Public keys',
                            subtitle: 'Manage the public keys used for Private Key JWT authentication',
                            empty: 'No public keys have been added yet.',
                            unavailable: {
                                title: 'Public keys are not active for this authentication method',
                                description:
                                    'Public keys are only used with Private Key JWT. Switch the authentication method to Private Key JWT to add or manage keys.',
                                descriptionWithKeys:
                                    'Public keys are only used with Private Key JWT. These keys are still stored and will be used again if you switch back.',
                            },
                            rsaLabel: 'RSA public key',
                            add: 'Add public key',
                            view: 'View public key',
                            remove: 'Remove',
                            removeConfirm: {
                                title: 'Remove public key',
                                message: 'Are you sure you want to remove the public key "{{kid}}"?',
                            },
                            dialog: {
                                title: 'Add public key',
                                kid: 'Key ID',
                                pem: 'Public key (PEM)',
                                help: 'Paste an RSA public key in PEM format. Leave the key ID empty to generate one automatically.',
                            },
                            detailsDialog: {
                                title: 'Public key details',
                                kid: 'Key ID',
                                type: 'Type',
                                publicKey: 'Public key',
                                publicKeyHelp: 'Review the stored public key or copy it for troubleshooting.',
                                copy: 'Copy public key',
                            },
                        },
                    },
                },
            },
            identities: {
                list: {
                    title: 'Identities',
                    sidebar: {
                        title: 'Identities',
                    },
                    toolbar: {
                        title: 'Identities',
                        counter_zero: 'No identities',
                        counter_one: '{{count, number}} identity',
                        counter_other: '{{count, number}} identities',
                        search: 'Primary email',
                        newIdentity: 'New identity',
                    },
                    empty: {
                        title: 'No identities yet',
                        subtitle: 'When users sign in to your applications, they will appear here.',
                        actionCreate: 'New identity',
                    },
                    newIdentity: {
                        title: 'New identity',
                        loadAuthProvidersError: 'Could not load authentication settings for this tenant.',
                        noLocalProvider:
                            'Email and password sign-in is not enabled for this tenant. Enable the local provider under Authentication before creating identities here.',
                        email: 'Primary email',
                        emailVerified: 'Email verified',
                        activate: 'Active (can sign in immediately)',
                        language: 'Language',
                        country: 'Country / region',
                        mfaRequiredHint:
                            'This tenant requires a second factor for active identities. Add an authenticator secret and/or enable the email MFA option.',
                        password: 'Password',
                        passwordHint:
                            'Required for active identities. Optional for pending identities (a temporary password is emailed instead).',
                        totpSecret: 'Authenticator (TOTP) secret',
                        totpSecretHint:
                            'Base32 secret from your authenticator enrollment flow, if you are registering TOTP now.',
                        includeEmailMfa: 'Register email MFA for this identity',
                        claimsJson: 'Custom claims (JSON object)',
                        claimsJsonHint:
                            'Optional. Flat string keys with JSON-serializable values, e.g. {"name":"Ada Lovelace"}.',
                        claimsInvalid: 'Claims must be a JSON object (not an array or primitive).',
                    },
                    table: {
                        columnIdentity: 'Identity',
                        columnStatus: 'Status',
                        columnDates: 'Activity',
                        columnProviders: 'Sign-in methods',
                        signedUpAt: 'Signed up',
                        lastSeenAt: 'Last seen',
                        emailVerified: 'Verified email',
                        locale: 'Locale',
                        statusValues: {
                            created: 'Created',
                            active: 'Active',
                            blocked: 'Blocked',
                        },
                        providers: {
                            local: 'Email / password',
                            google: 'Google',
                            microsoft: 'Microsoft',
                        },
                        actions: {
                            revokeAccess: 'Revoke sessions',
                            openMenu: 'Identity actions',
                            block: 'Block identity',
                            delete: 'Delete identity',
                        },
                        confirmRevokeAccess: {
                            title: 'Revoke sessions',
                            message:
                                '{{displayName}} will be signed out on every device and their tokens will stop working. They can sign in again as long as this identity is not blocked.',
                        },
                        confirmBlock: {
                            title: 'Block identity',
                            message:
                                '{{displayName}} will not be able to sign in. Active sessions will end and tokens will be revoked.',
                        },
                        confirmDelete: {
                            title: 'Delete identity',
                            message:
                                'Permanently delete {{displayName}} and their sign-in credentials? Active sessions will end and tokens will be revoked. Accounts in connected applications will not be deleted. This cannot be undone.',
                        },
                    },
                },
            },
            users: {
                title: 'Manage Administrators',
                inviteUser: 'Invite administrator',
                user: {
                    name: 'Name',
                    email: 'Email',
                    owner: 'Owner',
                    permissions: 'Permissions',
                    restrictions: 'Restrictions',
                    ownerAllPermissions: 'Owners have all permissions',
                    ownerNoRestrictions: 'Owners have no restrictions',
                },
                createInvitation: {
                    title: 'Administrator invitation',
                    invite: 'Invite',
                },
                list: {
                    title: 'Administrators',
                    counter_zero: '0 administrators',
                    counter_one: '{{count, number}} administrator',
                    counter_other: '{{count, number}} administrators',
                    name: 'Name',
                    email: 'Email',
                    owner: 'Owner',
                    edit: {
                        title: 'Edit administrator',
                        name: 'Name',
                        email: 'Email',
                    },
                    delete: {
                        title: 'Delete administrator',
                        message:
                            'Are you absolutely sure you want to delete the administrator "{{userName}}" from the current tenant?',
                    },
                },
                invitations: {
                    title: 'Invitations',
                    list: {
                        name: 'Name',
                        email: 'Email',
                        status: 'Status',
                        statusValue: {
                            pending: 'Pending',
                            delivered: 'Delivered',
                            error: 'Error',
                        },
                        resend: 'Resend',
                    },
                    delete: {
                        title: 'Delete invitation',
                        message: 'Are you absolutely sure you want to delete the invitation?',
                    },
                    empty: {
                        title: 'No pending invitations',
                        subtitle: 'Invitations are used to add new administrators to your tenant',
                        actionCreateInvitation: 'Invite an administrator',
                    },
                },
            },
            authProviders: {
                title: 'Sign-in methods',
                badge: 'Tenant access',
                limit: 'One setup per provider',
                heading: 'Choose how people can sign in',
                description:
                    'Set up the sign-in methods available for this tenant. Each provider can be connected once, then updated whenever you need to adjust the experience.',
                empty: {
                    title: 'No sign-in method is connected yet',
                    description:
                        'Users will not be able to sign in until you set up at least one provider for this tenant.',
                },
                status: {
                    connected: 'Enabled',
                    notConfigured: 'Not set up',
                },
                actions: {
                    setUp: 'Set up',
                    edit: 'Manage',
                    remove: 'Remove sign-in method',
                },
                values: {
                    enabled: 'Enabled',
                    disabled: 'Disabled',
                },
                dangerZone: {
                    cardTitle: 'Remove sign-in method',
                    cardDescription:
                        'If you remove {{method}} sign-in, anyone who currently uses it will no longer be able to authenticate with this tenant.',
                    confirmTitle: 'Remove {{method}} sign-in?',
                    confirmDescription:
                        'This will remove {{method}} sign-in from this tenant right away. Anyone who currently uses it will no longer be able to authenticate with this tenant.',
                },
                providers: {
                    local: {
                        title: 'Email & password',
                        cardDescription:
                            'Let people create an account with an email address and password, then layer in verification, MFA, and migration when needed.',
                        emptyMessage:
                            'This sign-in method is not configured yet. Set it up to allow direct email and password sign-in for this tenant.',
                        highlights: {
                            passwords: 'Passwords',
                            signup: 'Sign-up controls',
                            mfa: 'Multi-factor auth',
                        },
                        summary: {
                            passwords: 'Password policy',
                            signup: 'Sign-up',
                            emailVerification: 'Email verification',
                            mfa: 'MFA',
                            required: 'Required',
                            optional: 'Optional',
                        },
                        dialog: {
                            createTitle: 'Set up email & password',
                            editTitle: 'Edit email & password',
                        },
                        password: {
                            title: 'Password rules',
                            description: 'Decide how strong passwords should be and how password reset should work.',
                            strength: 'Password strength',
                            minLength: 'Minimum length',
                            resetTokenTtl: 'Reset link lifetime',
                            requireConfirmation: 'Require password confirmation',
                            strengthValues: {
                                none: 'No rules',
                                low: 'Low',
                                fair: 'Fair',
                                good: 'Good',
                                excellent: 'Excellent',
                            },
                        },
                        emailVerification: {
                            title: 'Email verification',
                            description:
                                'Choose whether email verification is used and how strict the requirement should be.',
                            enabled: 'Enable email verification',
                            requirement: 'Requirement',
                            tokenTtl: 'Verification link lifetime',
                            resendWait: 'Resend wait time',
                            requirementValues: {
                                optional: 'Optional',
                                required: 'Required',
                            },
                        },
                        signup: {
                            title: 'Sign-up',
                            description:
                                'Control whether new users can create an account and whether sign-up is protected.',
                            enabled: 'Allow people to create an account',
                            blockDisposableEmails: 'Block disposable email addresses',
                            captcha: {
                                enabled: 'Protect sign-up with CAPTCHA',
                                key: 'CAPTCHA site key',
                                secret: 'CAPTCHA secret',
                                minScore: 'Minimum score',
                            },
                        },
                        mfa: {
                            title: 'Multi-factor authentication',
                            description: 'Offer or require a second factor and decide which options are available.',
                            enabled: 'Enable multi-factor authentication',
                            requirement: 'Requirement',
                            promptAtSignup: 'Prompt during sign-up',
                            factors: 'Available factors',
                            requirementValues: {
                                optional: 'Optional',
                                required: 'Required',
                            },
                            factorValues: {
                                totp: 'Authenticator app',
                                email: 'Email code',
                            },
                        },
                        accountMigration: {
                            title: 'Account migration',
                            description:
                                'Connect a legacy identity store so users can be migrated the first time they sign in.',
                            enabled: 'Enable account migration',
                            authMethod: 'Validation method',
                            validationEndpoint: 'Validation endpoint',
                            confirmationEndpoint: 'Confirmation endpoint',
                            createIdentityWithMigrationId: 'Keep the migration ID on the created identity',
                            authMethodValues: {
                                selfAuth: 'Provider credentials',
                                apiKey: 'API key',
                                jwt: 'JWT',
                            },
                        },
                    },
                    google: {
                        title: 'Google',
                        cardDescription:
                            'Allow users to sign in with Google and control which profile data is requested and synced on each sign-in.',
                        emptyMessage:
                            'This sign-in method is not configured yet. Set it up to let people continue with their Google account.',
                        highlights: {
                            oauth: 'OAuth',
                            profile: 'Profile sync',
                            scopes: 'Custom scopes',
                        },
                        summary: {
                            clientId: 'Client ID',
                            scopes: 'Scopes',
                            scopeCount_one: '1 scope',
                            scopeCount_other: '{{count, number}} scopes',
                            sync: 'Profile sync',
                            syncOn: 'On every sign-in',
                            syncOff: 'Manual only',
                            emailVerification: 'Email verification',
                            required: 'Required',
                            optional: 'Optional',
                        },
                        dialog: {
                            createTitle: 'Set up Google sign-in',
                            editTitle: 'Edit Google sign-in',
                        },
                        connection: {
                            title: 'Connection',
                            description: 'Add the Google app credentials and choose which scopes should be requested.',
                            clientId: 'Client ID',
                            clientSecret: 'Client secret',
                            scopes: 'Scopes',
                            scopesPlaceholder: 'Type a scope and press enter...',
                            invalidScope: 'Scopes must be unique.',
                        },
                        emailVerification: {
                            title: 'Email verification',
                            description:
                                'Choose whether Google sign-ins should trigger Authy email verification and how strict the requirement should be.',
                            enabled: 'Enable email verification',
                            requirement: 'Requirement',
                            requirementValues: {
                                optional: 'Optional',
                                required: 'Required',
                            },
                            tokenTtl: 'Verification link lifetime',
                            resendWait: 'Resend cooldown',
                        },
                        profile: {
                            title: 'Profile sync',
                            description:
                                'Decide whether Google profile data should be refreshed whenever the user signs in.',
                            syncOnLogin: 'Sync profile data on sign-in',
                        },
                    },
                    microsoft: {
                        title: 'Microsoft',
                        cardDescription:
                            'Allow users to sign in with Microsoft and control which directory data is requested and synced on each sign-in.',
                        emptyMessage:
                            'This sign-in method is not configured yet. Set it up to let people continue with their Microsoft account.',
                        highlights: {
                            oauth: 'OAuth',
                            profile: 'Profile sync',
                            scopes: 'Custom scopes',
                        },
                        summary: {
                            clientId: 'Client ID',
                            scopes: 'Scopes',
                            scopeCount_one: '1 scope',
                            scopeCount_other: '{{count, number}} scopes',
                            sync: 'Profile sync',
                            syncOn: 'On every sign-in',
                            syncOff: 'Manual only',
                            emailVerification: 'Email verification',
                            required: 'Required',
                            optional: 'Optional',
                        },
                        dialog: {
                            createTitle: 'Set up Microsoft sign-in',
                            editTitle: 'Edit Microsoft sign-in',
                        },
                        connection: {
                            title: 'Connection',
                            description:
                                'Add the Microsoft app credentials and choose which extra scopes should be requested.',
                            clientId: 'Client ID',
                            clientSecret: 'Client secret',
                            scopes: 'Scopes',
                            scopesPlaceholder: 'Type a scope and press enter...',
                            invalidScope: 'Scopes must be unique.',
                        },
                        emailVerification: {
                            title: 'Email verification',
                            description:
                                'Choose whether Microsoft sign-ins should trigger Authy email verification and how strict the requirement should be.',
                            enabled: 'Enable email verification',
                            requirement: 'Requirement',
                            requirementValues: {
                                optional: 'Optional',
                                required: 'Required',
                            },
                            tokenTtl: 'Verification link lifetime',
                            resendWait: 'Resend cooldown',
                        },
                        profile: {
                            title: 'Profile sync',
                            description:
                                'Decide whether Microsoft profile data should be refreshed whenever the user signs in.',
                            syncOnLogin: 'Sync profile data on sign-in',
                        },
                    },
                },
            },
            apis: {
                title: 'APIs',
                heading: 'Manage tenant APIs',
                description:
                    'Set up the APIs available in this tenant, including their audience, token format, and requestable scopes.',
                status: {
                    enabled: 'Enabled',
                    disabled: 'Disabled',
                },
                actions: {
                    add: 'Add API',
                    manage: 'Manage',
                },
                empty: {
                    title: 'No APIs yet',
                    description:
                        'Add an API to define the audiences, access tokens, and scopes your applications can use.',
                    action: 'Add API',
                },
                card: {
                    noDescription: 'No description added yet.',
                    apiUrl: 'API URL',
                    audience: 'Token audience',
                    audienceUsesIndicator: 'Same as API URL',
                    accessTokens: 'Access tokens',
                    accessTokensValue: '{{format}} • {{ttl}} seconds',
                    scopes: 'Scopes',
                    scopeCount_zero: 'No scopes',
                    scopeCount_one: '{{count, number}} scope',
                    scopeCount_other: '{{count, number}} scopes',
                },
                dialog: {
                    createTitle: 'Add API',
                    editTitle: 'Edit API',
                    basic: {
                        title: 'Basics',
                        description: 'Choose a clear name and decide whether applications can request this API.',
                        enabled: 'API available',
                        name: 'Name',
                        descriptionField: 'Description',
                    },
                    resource: {
                        title: 'Resource',
                        description: 'Define the API URL and the audience value included in access tokens.',
                        indicator: 'API URL',
                        audienceMode: 'Token audience',
                        audienceModeValues: {
                            indicator: 'Use API URL',
                            custom: 'Custom audience',
                        },
                        audienceValue: 'Custom audience',
                    },
                    tokens: {
                        title: 'Access tokens',
                        description: 'Choose the token format applications receive when they request this API.',
                        format: 'Access token format',
                        formatValues: {
                            opaque: 'Opaque',
                            jwt: 'JWT',
                        },
                        ttl: 'Access token lifetime',
                        jwtHelp: 'JWT access tokens are signed with your tenant signing keys using RS256.',
                    },
                    scopes: {
                        title: 'Scopes',
                        description: 'Add the permissions applications can request for this API.',
                        empty: 'No scopes have been added yet.',
                        add: 'Add scope',
                        scopeFallback: 'Scope {{index}}',
                        name: 'Scope name',
                        icon: 'Icon',
                        display: 'Display',
                        displayTitle: 'Title',
                        displayDescription: 'Description',
                    },
                    remove: {
                        title: 'Remove API',
                        description:
                            'If you remove this API, applications that use it will no longer be able to request tokens for it. Existing application assignments will be removed automatically.',
                        action: 'Remove API',
                        confirmTitle: 'Remove API?',
                        confirmDescription:
                            'This removes {{name}} right away and clears it from any application that currently uses it.',
                    },
                },
            },
            settings: {
                title: 'Settings',
                general: {
                    title: 'General',
                    profile: {
                        title: 'Profile',
                        description: 'This information defines how the tenant is presented',
                        changeAvatar: 'Change avatar',
                        avatarInfo: 'JPG, GIF or PNG. 1MB max.',
                        name: 'Tenant name',
                        language: 'Default language',
                    },
                    localization: {
                        title: 'Localization',
                        description: 'Set the default locale and available languages for the authentication UI.',
                        language: 'Default language',
                        country: 'Default country',
                        availableLanguages: 'Available languages',
                    },
                    dangerZone: {
                        title: 'Danger zone',
                        description: 'Take care with the actions you perform here',
                        delete: {
                            title: 'Delete {{tenant}}',
                            description:
                                'Permanently delete all data from "{{tenant}}".\nAttention, this cannot be undone!',
                            action: 'Delete',
                            confirmation: {
                                title: 'Delete {{tenant}}',
                                message:
                                    'Are you absolutely sure that you want to delete "{{tenantName}}" and all associated data? This action can not be undone.',
                            },
                        },
                    },
                    slug: {
                        title: 'Identifier',
                        description:
                            'The identifier is used to provide a readable URL and is also used as the sender name in emails.',
                        label: 'Identifier',
                        updateSlug: 'Update Identifier',
                        note: 'Note: If your URL is already in use and you change your identifier, all old URLs will be invalidated.',
                    },
                },
                appearance: {
                    title: 'Appearance',
                },
                accountApp: {
                    title: 'Account App',
                    appearance: {
                        title: 'Appearance',
                        description: 'Customize the account app colors, light and dark themes, and favicon.',
                        favicon: 'Favicon',
                        faviconInfo: 'Uses the tenant avatar when no custom favicon is configured.',
                        allowDarkTheme: 'Allow dark theme',
                        darkThemeInfo: 'Follow the user’s system preference when enabled.',
                        schemes: {
                            light: 'Light theme',
                            dark: 'Dark theme',
                        },
                        colors: {
                            accent: 'Accent color',
                            pageBackground: 'Page background',
                            surfaceBackground: 'Surface background',
                        },
                    },
                    availability: {
                        title: 'Availability',
                        description: 'Let people manage their password and multi-factor authentication settings.',
                        status: {
                            enabled: 'Enabled',
                            disabled: 'Disabled',
                        },
                        content: {
                            enabledTitle: 'Account management is available for this tenant.',
                            enabledDescription:
                                'Identities can manage their password and multi-factor authentication settings in a dedicated account app.',
                            disabledTitle: 'Account management is currently disabled.',
                            disabledDescription:
                                'Saved settings and custom domain configuration are kept when the account app is disabled.',
                        },
                        actions: {
                            enable: 'Enable account management',
                            disable: 'Disable account management',
                        },
                        messages: {
                            enabled: 'Account management enabled.',
                            disabled: 'Account management disabled.',
                        },
                        open: 'Open account app',
                    },
                    customDomain: {
                        title: 'Custom account domain',
                        description: 'Serve this tenant account app from your own domain.',
                        domain: 'Domain',
                        stateDescription: {
                            none: 'Use your own domain for the account app. The regular Authy address remains available.',
                            pending:
                                'Your custom domain has been created. Complete the setup steps below to start using it.',
                            done: 'All setup steps are complete. Your custom domain is active.',
                        },
                        actions: {
                            remove: 'Remove custom domain',
                            setup: 'Setup custom domain',
                            refreshSetup: 'Refresh setup',
                            refreshSetupHint:
                                'DNS changes can take some time to propagate. If verification fails, wait a little and try again.',
                        },
                        removeConfirm: {
                            title: 'Remove custom account domain',
                            message: 'Are you sure you want to remove the custom account domain from this tenant?',
                        },
                    },
                },
                authentication: {
                    title: 'Authentication',
                    signup: {
                        title: 'Registration limit per IP',
                        description:
                            'Limit new accounts from the same IP address across all applications and sign-in methods. People sharing a network also share this allowance.',
                        enabled: 'Limit registrations per IP address',
                        maxRegistrations: 'Maximum registrations',
                        window: 'Time window',
                        windowChanged:
                            'When increasing the time window, the new limit may take up to the full duration of that window to become fully effective. During this period, some IP addresses may be able to register additional accounts beyond the configured limit.',
                    },
                    loginFlow: {
                        title: 'Login & Flow',
                        description: 'Configure how applications are allowed to authenticate against this tenant.',
                        allowAuthTroughIframe: 'Allow authentication through iframe',
                        allowImplicitFlow: 'Allow implicit flow',
                    },
                    customDomain: {
                        title: 'Custom auth domain',
                        description: 'Serve this tenant authentication experience from your own domain.',
                        domain: 'Domain',
                        dnsRecord: {
                            type: 'Type',
                            name: 'Name',
                            value: 'Value',
                            status: 'Status',
                            verified: 'Verified',
                            pending: 'Pending',
                        },
                        stateDescription: {
                            none: 'Configure a custom domain for login, signup, and password recovery pages. Each tenant supports one custom auth domain.',
                            pending:
                                'Your custom domain has been created. Complete the setup steps below to start using it.',
                            done: 'All setup steps are complete. Your custom domain is active.',
                        },
                        actions: {
                            remove: 'Remove custom domain',
                            setup: 'Setup custom domain',
                            refreshSetup: 'Refresh setup',
                            refreshSetupHint:
                                'DNS changes can take some time to propagate. If verification fails, wait a little and try again.',
                        },
                        removeConfirm: {
                            title: 'Remove custom auth domain',
                            message: 'Are you sure you want to remove the custom auth domain from this tenant?',
                        },
                    },
                    session: {
                        title: 'Session',
                        description: 'Control session persistence and expiration behavior.',
                        persistent: 'Persistent sessions',
                        persistenceMode: 'Persistence mode',
                        persistentOptions: {
                            persistent: {
                                title: 'Persistent',
                                description: 'Sessions can survive browser restarts until they expire or are revoked.',
                            },
                            sessionOnly: {
                                title: 'Session only',
                                description:
                                    'Sessions end when the browser is closed, even if the lifetime has not elapsed.',
                            },
                        },
                        idleTtl: 'Idle timeout',
                        maxTtl: 'Maximum lifetime',
                    },
                    logout: {
                        title: 'Logout',
                        description: 'Configure the logout experience for end users.',
                        requireLogoutConfirmation: 'Require logout confirmation',
                    },
                },
                scopes: {
                    title: 'Scopes & Claims',
                    scopes: {
                        title: 'Scopes',
                        description: 'Manage which scopes are available and which claims they expose.',
                        empty: 'No scopes configured yet.',
                        scopeFallback: 'Scope {{index}}',
                        add: 'Add scope',
                        name: 'Scope name',
                        icon: 'Icon',
                        claims: 'Claims',
                        claimsPlaceholder: 'Type a claim name and press enter...',
                        display: {
                            title: 'Display',
                            language: 'Language',
                            fieldTitle: 'Title',
                            fieldDescription: 'Description',
                            completion: '{{count}} / {{total}} languages ready',
                        },
                        errors: {
                            invalidClaim: 'Claims must be unique and use a valid claim identifier.',
                        },
                    },
                    additionalClaims: {
                        title: 'Additional Claims',
                        description: 'Expose extra claims that are not attached to a dedicated scope.',
                        label: 'Claims',
                        placeholder: 'Type a claim name and press enter...',
                        errors: {
                            invalidClaim: 'Claims must be unique and use a valid claim identifier.',
                        },
                    },
                },
                security: {
                    title: 'Security',
                    cookies: {
                        title: 'Cookies',
                        description: 'Manage cookie signing material and automatic rotation behavior.',
                        availableKeys_one: '1 cookie key is currently available.',
                        availableKeys_other: '{{count}} cookie keys are currently available.',
                        automation: {
                            title: 'Automatic rotation',
                            description: 'Rotate cookie keys automatically on a schedule.',
                            retentionNote:
                                'Older keys remain available temporarily after rotation and are removed once the stored-key limit is reached.',
                            nextRotation: 'Next automatic rotation: {{date}}',
                        },
                        autoRotate: 'Auto rotate keys',
                        rotationInterval: 'Rotation interval',
                        maxKeys: 'Maximum stored keys',
                        management: {
                            rotate: 'Rotate',
                            rotateTitle: 'Rotate cookie keys',
                            rotateDescription:
                                'Generate a new active cookie key and keep older keys until the stored-key limit is reached.',
                        },
                        rotateConfirm: {
                            title: 'Rotate cookie keys?',
                            message:
                                'A new cookie key will become active immediately, and older keys will be kept only up to the configured limit.',
                        },
                    },
                    signingKeys: {
                        title: 'Signing Keys',
                        description: 'Manage automatic rotation for the tenant signing keys.',
                        empty: 'No signing keys are currently available.',
                        list: {
                            title: 'Available signing keys',
                            description:
                                'Review key state here and open the action menu for details and management actions.',
                            showRevoked_one: 'Show 1 revoked key',
                            showRevoked_other: 'Show {{count}} revoked keys',
                            hideRevoked: 'Hide revoked keys',
                        },
                        automation: {
                            title: 'Automatic rotation',
                            description:
                                'Configure whether signing keys should rotate automatically and how often a new key should be prepared.',
                            retentionNote:
                                'Previous keys are kept temporarily for verification after rotation and are automatically revoked later.',
                            nextRotation: 'Next automatic rotation: {{date}}',
                        },
                        autoRotate: 'Auto rotate signing keys',
                        rotationInterval: 'Rotation interval',
                        statusValues: {
                            current: 'Current',
                            next: 'Next',
                            previous: 'Previous',
                            revoked: 'Revoked',
                        },
                        actions: {
                            openMenu: 'Open actions',
                            details: 'Details',
                            revoke: 'Revoke',
                        },
                        management: {
                            title: 'Manual key management',
                            description:
                                'Use these actions when you need to rotate the signing keys before the scheduled interval.',
                            rotate: 'Rotate',
                            rotateTitle: 'Rotate keys',
                            rotateDescription:
                                'Promote the prepared next signing key to current and create a fresh next key. The rotated-out key remains available for verification.',
                            rotateAndRevoke: 'Rotate and revoke',
                            rotateAndRevokeTitle: 'Rotate and revoke the current key',
                            rotateAndRevokeDescription:
                                'Promote the next signing key, create a fresh next key, and immediately revoke the current key that is being replaced.',
                            unavailable: 'Manual rotation requires both a current and a next signing key.',
                        },
                        detailsDialog: {
                            title: 'Signing key details',
                            id: 'ID',
                            status: 'Status',
                            createdAt: 'Created',
                            revokedAt: 'Revoked',
                            publicKey: 'Public key',
                            publicKeyHelp: 'Use this key anywhere signature verification is required.',
                            copy: 'Copy public key',
                            notRevoked: 'Not revoked',
                            unknownDate: 'Unknown',
                        },
                        revokeConfirm: {
                            title: 'Revoke signing key',
                            current:
                                'Are you sure you want to revoke signing key {{keyId}}? The next key will be promoted to current and a fresh next key will be created automatically.',
                            next: 'Are you sure you want to revoke signing key {{keyId}}? A replacement next key will be created automatically.',
                            previous:
                                'Are you sure you want to revoke signing key {{keyId}}? Revoked keys stay visible for reference but can no longer be used.',
                        },
                        rotateConfirm: {
                            title: 'Rotate signing keys',
                            message: 'This promotes the next signing key to current and creates a fresh next key.',
                        },
                        rotateAndRevokeConfirm: {
                            title: 'Rotate and revoke signing keys',
                            message:
                                'This promotes the next signing key to current, creates a fresh next key, and revokes the key that was rotated out.',
                        },
                    },
                },
                communication: {
                    title: 'Communication',
                    emailSender: {
                        title: 'Email sender',
                        description: 'Configure how emails from this tenant appear.',
                        senderName: 'Sender name',
                        senderAddressPrefix: 'Sender address prefix',
                        replyToAddress: 'Reply-to address',
                    },
                    emailDomain: {
                        title: 'Custom sending domain',
                        description: 'Send emails from a domain you control.',
                        domain: 'Domain',
                        setupDescription:
                            'Configure a custom domain for sending email. Each tenant supports one custom sending domain.',
                        stateDescription: {
                            pending: 'Complete the setup steps below to start sending email from this domain.',
                            done: 'All setup steps are complete. Your custom sending domain is active.',
                        },
                        setupProgress: {
                            dns: {
                                title: 'DNS verification',
                                description: 'Add the following DNS records at your DNS provider.',
                            },
                            webhooks: {
                                title: 'Delivery status updates',
                                description: 'Authy configures delivery and permanent failure updates.',
                            },
                            error: {
                                occurredAt: 'Last failed {{date}}',
                            },
                        },
                        lastCheckedAt: 'Last setup check: {{date}}.',
                        actions: {
                            setup: 'Set up domain',
                            refresh: 'Refresh setup',
                            remove: 'Remove domain',
                        },
                        removeConfirm: {
                            title: 'Remove custom sending domain',
                            message: 'Remove this domain? Email will be sent from the default domain afterwards.',
                        },
                    },
                    emailTemplates: {
                        title: 'Email templates',
                        description:
                            'Adjust the subject line, inbox preview, and layout for each email Authy sends on behalf of this tenant.',
                        items: {
                            accountActivation: {
                                title: 'Account activation',
                                description:
                                    'Sent when an identity is created through the API with a temporary password.',
                            },
                            emailVerification: {
                                title: 'Email verification',
                                description: 'Sent when someone needs to confirm their email address.',
                            },
                            passwordReset: {
                                title: 'Password reset',
                                description: 'Sent when someone asks to reset their password.',
                            },
                            mfaEmailCode: {
                                title: 'Verification code',
                                description: 'Sent when a one-time email code is used during sign-in.',
                            },
                        },
                        fields: {
                            subject: 'Subject',
                            previewText: 'Preview text',
                            previewTextInfo: 'Shown as preview in inboxes that support it.',
                        },
                        emptySubject: 'No subject',
                        emptyPreviewText: 'No preview text',
                        previewError: 'Preview could not be loaded.',
                        previewEmpty: 'No preview available.',
                        actions: {
                            edit: 'Edit template',
                            sendTest: 'Send test',
                        },
                        dialog: {
                            title: 'Edit {{template}}',
                            unsavedChanges: {
                                title: 'Discard unsaved changes?',
                                message:
                                    'Switching languages will discard the changes you made for the current language.',
                            },
                        },
                        sendTest: {
                            title: 'Send test email',
                            recipientEmail: 'Recipient email',
                            action: 'Send test email',
                            success: 'Test email sent to {{email}}',
                        },
                    },
                },
            },
            developer: {
                title: 'Developer',
                apiDocumentation: {
                    title: 'Api Documentation',
                    description:
                        'Use the guides to get started with the api including authentication then use the api reference to get an overview of all available REST endpoints.',
                    tenantId: 'Your tenant id: {{tenantId}}',
                    linkReference: 'View reference',
                },
                navigation: {
                    apiKeys: 'API Keys',
                    apiLogs: 'API Logs',
                    failureLogs: 'Failure Logs',
                },
                apiKeys: {
                    title: 'Manage API Keys',
                    toolbar: {
                        title: 'API Keys',
                        counter_zero: 'No API Keys',
                        counter_one: 'One API Keys',
                        counter_other: '{{count, number}} API Keys',
                        search: 'Search for an API Key',
                        newApiKey: 'Create API Key',
                    },
                    empty: {
                        title: 'No API Keys',
                        subtitle:
                            'You have no API Keys created yet. Create one now to access the REST api with different scopes.',
                        actionCreate: 'Create an API Key',
                    },
                    description: 'The API Keys let you access the REST api with different scopes',
                    apiKey: {
                        name: 'Name',
                        apiKey: 'API Key',
                        lastSeenAt: 'Last use',
                        lastSeenAtNever: 'Never',
                    },
                    create: 'Create API Key',
                    delete: {
                        title: 'Delete API Key',
                        message:
                            'Are you absolutely sure you want to permanently delete the API Key "{{apiKeyIdentificator}}". This cannot be undone!',
                    },
                    createEditApiKey: {
                        create: {
                            title: 'Create a new API Key',
                            createApiKey: 'Create API Key',
                        },
                        edit: {
                            title: 'Edit API Key',
                        },
                        description: 'Description',
                        language: 'Language',
                    },
                    showApiKey: {
                        title: 'API Key created',
                        message: 'Please copy and save this API Key as it will never be shown again.',
                    },
                },
                failureLogs: {
                    title: 'Failure Logs',
                    toolbar: {
                        counter_zero: 'No failures',
                        counter_one: 'One failure',
                        counter_other: '{{count, number}} failures',
                        search: 'Search by tracking ID, details or IP address',
                        refresh: 'Refresh',
                        allKinds: 'All types',
                        allCodes: 'All codes',
                    },
                    kinds: { app: 'Application', protocol: 'Protocol' },
                    filter: { toCreatedAt: 'Until' },
                    fields: {
                        createdAt: 'Occurred at',
                        trackId: 'Tracking ID',
                        kind: 'Type',
                        code: 'Failure code',
                        scope: 'Context',
                        ipAddress: 'IP address',
                        method: 'HTTP method',
                        details: 'Diagnostic details',
                        sentryEventId: 'Sentry event ID',
                    },
                    empty: {
                        title: 'No failure logs found',
                        subtitle: 'There are no recorded failures matching your search and filters.',
                    },
                    details: { title: 'Failure details', none: 'Select a failure for more details' },
                },
                apiLogs: {
                    title: 'Manage API Logs',
                    toolbar: {
                        title: 'API Logs',
                        counter_zero: 'No API Logs',
                        counter_one: 'One API Logs',
                        counter_other: '{{count, number}} API Logs',
                        search: 'Search for API Logs',
                        refresh: 'Refresh',
                        status: {
                            label: 'Status',
                            all: 'All statuses',
                            success: 'Success',
                            error: 'Error',
                        },
                        method: {
                            label: 'Method',
                            all: 'All methods',
                        },
                    },
                    filter: {
                        fromRequestedAt: 'Requested at',
                    },
                    empty: {
                        title: 'No API Logs',
                        subtitle: 'Find out about what API calls are being made and keep track if its responses.',
                    },
                    description:
                        'The API Logs let you find out about what API calls are being made and keep track if its responses.',
                    apiLog: {
                        method: 'Method',
                        requestUrl: 'Request URL',
                        apiKey: 'API Key',
                        requestedAt: 'Requested at',
                        duration: 'Duration',
                        responseStatusCode: 'Response code',
                        durations: {
                            millisecondShort: '{{value}} ms',
                            secondShort: '{{value}} s',
                            minuteShort: '{{value}} min',
                            hourShort: '{{value}} h',
                        },
                    },
                    details: {
                        title: {
                            title: 'Details of the API Log',
                            none: 'Select an API Log for more details',
                        },
                        info: {
                            requestUrl: 'Request URL',
                            ipAddress: 'IP Address',
                            finishedAt: 'Finished at',
                            requestPayload: 'Request',
                            responsePayload: 'Response',
                            apiKey: 'API Key',
                            requestedAt: 'Requested at',
                            duration: 'Duration',
                            procedure: 'Procedure',
                            responseStatusCode: 'Response code',
                        },
                    },
                },
            },
        },
        user: {
            profile: {
                title: 'Profile',
                personal: {
                    title: 'Personal information',
                    description: 'This information is visible to other administrators in your tenant',
                    avatar: 'avatar',
                    avatarInfo: 'JPG, GIF or PNG. 1MB max.',
                    name: 'Your full name',
                    email: 'Your E-Mail address',
                    changeEmail: 'Change E-Mail',
                    language: 'Your preferred language',
                    bio: 'A little bit about yourself',
                },
                emailChangeDialog: {
                    title: 'Change your E-Mail',
                    email: 'New E-Mail address',
                    save: 'Save',
                },
                security: {
                    title: 'Security',
                    description: 'Change your password and configure multi-factor authentication methods',
                    securitySettings: 'Open security settings',
                },
                'ui-preferences': {
                    title: 'UI Preferences',
                    description:
                        'Setup your UI-Preferences which will only apply to this session and will not affect any other instances of the app',
                    colorScheme: 'Your preferred color scheme',
                    colorSchemeValue: {
                        system: 'Use the system defaults',
                        light: 'Use light color scheme',
                        dark: 'Use dark color scheme',
                    },
                },
            },
        },
        components: {
            customDomainSetupProgress: {
                dns: {
                    title: 'DNS records',
                    description: 'Add the required records to prove that you control this domain.',
                },
                certificate: {
                    title: 'Certificate',
                    description: 'Authy creates and waits for a TLS certificate after DNS is verified.',
                },
                routing: {
                    title: 'Routing',
                    description: 'Authy creates the routing configuration after the certificate is ready.',
                },
                error: {
                    occurredAt: 'Occurred at {{date}}.',
                },

                lastCheckedAt: 'Last verification check: {{date}}.',
            },
            durationInput: {
                duration: 'Duration',
                format: 'Format',
                segmentLabel: '{{label}}: {{unit}}',
                units: {
                    days: 'd',
                    hours: 'h',
                    minutes: 'm',
                    seconds: 's',
                },
            },
            urlListEditor: {
                add: 'Add URL',
                remove: 'Remove URL',
                invalidUrl: 'Invalid URL',
            },
            presenter: {
                presenterType: {
                    title: 'Authentication experience',
                    description: 'Choose how authentication pages are displayed.',
                    content: {
                        inherit: {
                            title: 'Tenant settings are active.',
                            description:
                                'This application uses the authentication experience, theme, and design configured for the tenant.',
                        },
                        static: {
                            title: 'Static pages are active.',
                            description: 'Authentication is shown with simple server-rendered pages and fixed styling.',
                        },
                        spa: {
                            title: 'Interactive app is active.',
                            description:
                                'Authentication is shown in a dynamic app with customizable theme and design options.',
                        },
                    },
                    actions: {
                        change: 'Change experience',
                    },
                    dialog: {
                        title: 'Change authentication experience',
                        action: 'Change experience',
                        warning: {
                            restoreInheritance:
                                'Custom appearance settings will be removed. This application will use the tenant settings.',
                            inheritToStatic:
                                'This application will use static authentication pages. The tenant settings will remain unchanged.',
                            spaToStatic: 'Theme and design settings will be removed.',
                            staticToSpa:
                                'A new interactive app will be created with default theme and design settings.',
                        },
                    },
                },
                spaTheme: {
                    title: 'Theme',
                    description:
                        'Configure logos, colors, the favicon, and light or dark variants for the interactive app.',
                    favicon: 'Favicon',
                    faviconInfo: 'Uses the tenant avatar when no custom favicon is configured.',
                    allowDarkTheme: 'Allow dark theme',
                    darkThemeInfo: 'Follow the user’s system preference when enabled.',
                    schemes: {
                        light: 'Light theme',
                        dark: 'Dark theme',
                    },
                    colors: {
                        accent: 'Accent color',
                        pageBackground: 'Page background',
                        surfaceBackground: 'Surface background',
                    },
                },
                spaDesign: {
                    title: 'Design',
                    description: 'Choose the authentication app layout and configure design-specific content.',
                    splitImage: 'Side image',
                    designs: {
                        default: {
                            label: 'Default',
                            description: 'Dedicated sign-in and sign-up pages with the standard layout.',
                        },
                        combined: {
                            label: 'Combined',
                            description: 'A single welcome screen where users choose sign-in or sign-up.',
                        },
                        split: {
                            label: 'Split',
                            description: 'Sign-in and sign-up forms beside a large visual area.',
                        },
                    },
                },
                combinedStartPage: {
                    infoTitle: 'Info title',
                    infoItems: 'Info items',
                    infoItemPlaceholder: 'Enter an item',
                    addInfoItem: 'Add item',
                    removeInfoItem: 'Remove item',
                },
                presenter: {
                    inherit: {
                        label: 'Use tenant settings',
                        description: 'Follow the authentication experience configured for the tenant.',
                    },
                    static: {
                        label: 'Static pages',
                        description: 'Simple server-rendered authentication pages with fixed styling.',
                    },
                    spa: {
                        label: 'Interactive app',
                        description: 'Dynamic authentication pages with tenant logos, colors, and design options.',
                    },
                },
            },
            domainDnsRecordCard: {
                name: 'Name',
                value: 'Value',
                verified: 'Verified',
                pending: 'Pending',
            },
            tenantPermissionsEditor: {
                searchPlaceholder: 'Type to search for permissions...',
                noScopeFound: 'No scope found.',
                noAccess: 'No access',
                fullAccess: 'Full access',
                partialAccess: 'Partial access',
            },
            tenantRestrictionsEditor: {
                add: 'Type the "{{scope}}" ID to add a new restriction...',
                errors: {
                    alreadyExists: 'Already exists',
                    invalidId: 'Invalid ID',
                },
            },
            customRestrictionsEditor: {
                membershipSections: {
                    label: 'Members by Membership Sections',
                    allowEmpty: 'Allow access members without sections',
                    enabled: 'Enabled',
                    disabled: 'Disabled',
                    hint: {
                        disabled:
                            'The restriction is not enabled. The administrator can access all members regardless of the sections.',
                        noneSelected: 'No sections have been selected. No one member can be accessed.',
                        noneSelectedAllowEmpty:
                            'No sections have been selected. Only members without sections can be accessed.',
                        selected: 'Only members with the selected sections can be accessed.',
                        selectedAllowEmpty:
                            'Only members who have either the selected sections or no sections assigned can be accessed.',
                    },
                    noAccess:
                        'The administrator must have at least the permissions to read members and their sections in order to have these restrictions.',
                },
            },
        },
    },
}
